import React, { createContext, useContext, useRef, useCallback, ReactNode } from 'react';

interface UnsavedChangesContextValue {
  /**
   * 저장 함수를 등록합니다.
   * @param saveFn 저장 함수 (Promise를 반환해야 함)
   * @param isDirty 변경사항이 있는지 여부를 반환하는 함수
   * @param canSave 저장 가능 여부를 반환하는 함수 (선택적, 기본값: () => true)
   * @returns 등록 해제 함수
   */
  registerSaveHandler: (
    saveFn: () => Promise<void>,
    isDirty: () => boolean,
    canSave?: () => boolean
  ) => () => void;
  /**
   * 등록된 저장 함수를 실행합니다.
   * @returns 저장 성공 여부
   */
  saveAll: () => Promise<boolean>;
  /**
   * 변경사항이 있는지 확인합니다.
   */
  hasUnsavedChanges: () => boolean;
  /**
   * 저장이 가능한 변경사항이 있는지 확인합니다.
   * 변경사항이 있고, 모든 핸들러가 저장 가능한 경우에만 true를 반환합니다.
   */
  hasSaveableChanges: () => boolean;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(null);

/**
 * 전역 저장 컨텍스트 Provider
 *
 * 편집 중인 내용을 전역적으로 추적하고 저장할 수 있게 해주는 컨텍스트입니다.
 * 동시접속 감지 시 자동 저장에 사용됩니다.
 *
 * @example
 * ```tsx
 * // App.tsx
 * <UnsavedChangesProvider>
 *   <SimultaneousAccessProvider>
 *     <Router>...</Router>
 *   </SimultaneousAccessProvider>
 * </UnsavedChangesProvider>
 * ```
 */
export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  // 저장 함수와 isDirty 함수를 저장하는 ref
  const saveHandlersRef = useRef<
    Array<{
      saveFn: () => Promise<void>;
      isDirty: () => boolean;
      canSave: () => boolean;
    }>
  >([]);

  const registerSaveHandler = useCallback(
    (saveFn: () => Promise<void>, isDirty: () => boolean, canSave: () => boolean = () => true) => {
      const handler = { saveFn, isDirty, canSave };
      saveHandlersRef.current.push(handler);

      // 등록 해제 함수 반환
      return () => {
        const index = saveHandlersRef.current.indexOf(handler);
        if (index > -1) {
          saveHandlersRef.current.splice(index, 1);
        }
      };
    },
    []
  );

  const hasUnsavedChanges = useCallback(() => {
    return saveHandlersRef.current.some((handler) => handler.isDirty());
  }, []);

  const hasSaveableChanges = useCallback(() => {
    const dirtyHandlers = saveHandlersRef.current.filter((handler) => handler.isDirty());
    if (dirtyHandlers.length === 0) {
      return false;
    }
    // 모든 dirty 핸들러가 저장 가능해야 함
    return dirtyHandlers.every((handler) => handler.canSave());
  }, []);

  const saveAll = useCallback(async (): Promise<boolean> => {
    const handlers = saveHandlersRef.current.filter(
      (handler) => handler.isDirty() && handler.canSave()
    );

    if (handlers.length === 0) {
      return true; // 저장할 내용이 없으면 성공으로 간주
    }

    try {
      // 모든 저장 함수를 병렬로 실행
      await Promise.all(handlers.map((handler) => handler.saveFn()));
      return true;
    } catch (error) {
      console.error('자동 저장 중 오류 발생:', error);
      return false;
    }
  }, []);

  const value: UnsavedChangesContextValue = {
    registerSaveHandler,
    saveAll,
    hasUnsavedChanges,
    hasSaveableChanges
  };

  return <UnsavedChangesContext.Provider value={value}>{children}</UnsavedChangesContext.Provider>;
}

/**
 * 전역 저장 컨텍스트를 사용하는 훅
 *
 * @example
 * ```tsx
 * const { registerSaveHandler } = useUnsavedChanges();
 *
 * useEffect(() => {
 *   const unregister = registerSaveHandler(
 *     async () => {
 *       await handleSubmit(onSubmit)();
 *     },
 *     () => formState.isDirty,
 *     () => canSave // 저장 가능 여부 (선택적)
 *   );
 *
 *   return unregister;
 * }, [formState.isDirty, handleSubmit, onSubmit, canSave]);
 * ```
 */
export function useUnsavedChanges(): UnsavedChangesContextValue {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error('useUnsavedChanges must be used within UnsavedChangesProvider');
  }
  return context;
}
